"use client"

import { useQuery } from "@tanstack/react-query"
import Link from "next/link"
import type { ComponentProps } from "react"
import {
  buildProductReviewPermissionQueryKey,
  getProductReviewPermission,
} from "@/api/product-reviews"
import { Button } from "@/components/ui/button"
import { cn } from "@/utilities"

interface ReviewActionsProps extends ComponentProps<"div"> {
  productId: number
  productPermalink: string
}

export function ReviewActions({
  productId,
  productPermalink,
  className,
  ...props
}: ReviewActionsProps) {
  const permissionQuery = useQuery({
    queryKey: buildProductReviewPermissionQueryKey(productId),
    queryFn: () => getProductReviewPermission(productId),
    retry: false,
  })

  if (permissionQuery.isPending) {
    return null
  }

  if (!permissionQuery.data?.ok) {
    return null
  }

  const { canReview, reason } = permissionQuery.data

  // User purchased and can write a review
  if (canReview && reason === "purchased") {
    return (
      <div className={cn("flex items-center gap-2", className)} {...props}>
        <Button asChild size='sm' variant='outline'>
          <Link href={`${productPermalink}?tab=reviews&writeReview=1`}>
            Avaliar
          </Link>
        </Button>
      </div>
    )
  }

  // User already reviewed this product
  if (!canReview && reason === "already_reviewed") {
    return (
      <div className={cn("flex items-center gap-2", className)} {...props}>
        <Button asChild size='sm' variant='outline'>
          <Link href={`${productPermalink}?tab=reviews`}>
            Ver minha avaliação
          </Link>
        </Button>
      </div>
    )
  }

  return null
}
